/**
 * "Command finished" notifications (Terminal board card 5, 21.10.2026), as
 * iTerm2, Warp and kitty do: when a command ran for a while and you were not
 * looking — the app window is in the background or the pane is hidden —
 * a desktop notification says it is done. Pure rules; the timing is in
 * utils/terminalPromptMarks.js, the toast in utils/terminalNotification.js.
 */

export const NOTIFY_DEFAULT_SECONDS = 15;

/** Seconds as stored: a whole number 3–3600, else the default. */
export function clampNotifySeconds(v) {
  const n = Math.round(Number(v));
  if (!Number.isFinite(n)) return NOTIFY_DEFAULT_SECONDS;
  return Math.min(3600, Math.max(3, n));
}

/**
 * Whether to notify for a finished command. `prefs` = { on, seconds,
 * desktop } (desktop = the app-wide desktop notification switch).
 */
export function shouldNotify({ ms, appFocused, paneVisible }, prefs) {
  if (!prefs || prefs.on === false || prefs.desktop === false) return false;
  if (!(ms >= clampNotifySeconds(prefs.seconds) * 1000)) return false;
  return !appFocused || !paneVisible;
}

/** 9s · 1m 12s · 1h 3m */
export function formatDuration(ms) {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return s % 60 ? `${m}m ${s % 60}s` : `${m}m`;
  return m % 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${Math.floor(m / 60)}h`;
}

/**
 * The command as typed. When the command sits on the prompt's own line
 * (`samePromptLine`), the prompt in front of it goes — everything up to the
 * first prompt sign (❯ › $ # % >) and its space. A command on a line of its
 * own (our Windows prompts put the path on the line above) is kept whole, so
 * `npm run build > out.txt` stays intact. Long lines are cut to 80 chars.
 */
export function commandLabel(line, samePromptLine) {
  let s = String(line || '').trim();
  if (samePromptLine) {
    // The sign ends the line on an empty prompt (the line is trimmed).
    const m = s.match(/^.*?[❯›$#%>](?:\s+|$)/u);
    if (m) s = s.slice(m[0].length);
  }
  return s.length > 80 ? `${s.slice(0, 79)}…` : s;
}
